import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { getWhatsAppNumber } from '@/lib/phone';
import { MessageCircle, Mail, Pencil, Upload, Link2 } from 'lucide-react';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { generateInviteToken, hashToken } from '@/lib/inviteToken';
import type { Candidate } from '@/types';

const DEFAULT_EXPIRY_DAYS = 7;

/** Candidato parseado del CSV (para previsualización antes de guardar). */
interface ParsedCandidate {
  fullName: string;
  email: string;
  phone?: string;
}

export function CandidatesPage() {
  const { id } = useParams<{ id: string }>();
  const { appUser, canAccessCompany, companyCountry, canWriteCompanyData } = useAuth();
  const [processTitle, setProcessTitle] = useState('');
  const [processStatus, setProcessStatus] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [sendingCandidateId, setSendingCandidateId] = useState<string | null>(null);
  const [copyingLinkId, setCopyingLinkId] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formFullName, setFormFullName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPhone, setFormPhone] = useState<string | undefined>('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [parsedCandidates, setParsedCandidates] = useState<ParsedCandidate[]>([]);
  const [importingFile, setImportingFile] = useState(false);
  const [parsingError, setParsingError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);

  const handleEdit = (c: Candidate) => {
    if (!canWriteCompanyData) return;
    setFormFullName(c.fullName ?? '');
    setFormEmail(c.email ?? '');
    setFormPhone(c.phone ?? '');
    setEditingId(c.id);
    setEditModalOpen(true);
  };

  const handleUpdateCandidate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canWriteCompanyData) return;
    if (!editingId) return;
    setIsUpdating(true);
    try {
      await updateDoc(doc(db, 'candidates', editingId), {
        fullName: formFullName.trim() || null,
        email: formEmail.trim() || null,
        phone: formPhone?.trim() || null,
      });
      setCandidates((prev) =>
        prev.map((c) =>
          c.id === editingId
            ? {
                ...c,
                fullName: formFullName.trim() || undefined,
                email: formEmail.trim() || undefined,
                phone: formPhone?.trim() || undefined,
              }
            : c
        )
      );
      setEditModalOpen(false);
      setEditingId(null);
      setFormFullName('');
      setFormEmail('');
      setFormPhone('');
    } catch (err) {
      console.error('Error al actualizar candidato:', err);
      window.alert(err instanceof Error ? err.message : 'Error al actualizar el candidato');
    } finally {
      setIsUpdating(false);
    }
  };

  const createInviteForCandidate = async (fullName: string, email: string, phone?: string): Promise<void> => {
    if (!canWriteCompanyData) throw new Error('No tienes permisos para invitar candidatos.');
    const companyId = appUser?.companyId;
    if (!id || !companyId) throw new Error('Faltan datos');
    const token = generateInviteToken();
    const tokenHash = await hashToken(token);
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + DEFAULT_EXPIRY_DAYS);

    const candidateRef = await addDoc(collection(db, 'candidates'), {
      companyId,
      processId: id,
      fullName: fullName?.trim() || null,
      email: email?.trim() || null,
      phone: phone?.trim() || null,
      status: 'invited',
      createdAt: new Date().toISOString(),
    });

    const inviteRef = await addDoc(collection(db, 'invites'), {
      companyId,
      processId: id,
      candidateId: candidateRef.id,
      tokenHash,
      tokenExpiresAt: expiresAt.toISOString(),
      createdAt: new Date().toISOString(),
    });

    await setDoc(doc(db, 'inviteTokenLookup', tokenHash), {
      inviteId: inviteRef.id,
      candidateId: candidateRef.id,
      processId: id,
      companyId,
      tokenExpiresAt: expiresAt.toISOString(),
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canWriteCompanyData) return;
    const file = e.target.files?.[0];
    if (!file || !id || !appUser?.companyId) return;
    setImportingFile(true);
    setParsingError(null);
    setParsedCandidates([]);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = (reader.result as string) ?? '';
        const lines = text.split(/\r?\n/).filter((line) => line.trim());
        if (lines.length < 2) {
          setParsingError('El archivo debe tener al menos una fila de encabezados y una de datos.');
          setImportingFile(false);
          return;
        }
        const headerLine = lines[0];
        const delimiter = headerLine.includes(';') ? ';' : ',';
        const parseCsvLine = (line: string): string[] => {
          const result: string[] = [];
          let current = '';
          let inQuotes = false;
          for (let i = 0; i < line.length; i++) {
            const c = line[i];
            if (c === '"') inQuotes = !inQuotes;
            else if (c === delimiter && !inQuotes) {
              result.push(current.trim());
              current = '';
            } else current += c;
          }
          result.push(current.trim());
          return result;
        };
        const headers = parseCsvLine(headerLine).map((h) => h.toLowerCase().trim());
        const findCol = (...names: string[]): number => {
          for (const n of names) {
            const idx = headers.indexOf(n);
            if (idx >= 0) return idx;
          }
          return -1;
        };
        const nombreIdx = findCol('nombre');
        const emailIdx = findCol('email');
        const telefonoIdx = findCol('teléfono', 'telefono', 'tel');
        const list: ParsedCandidate[] = [];
        for (let i = 1; i < lines.length; i++) {
          const cells = parseCsvLine(lines[i]);
          const fullName = (nombreIdx >= 0 ? cells[nombreIdx] ?? '' : '').trim();
          const email = (emailIdx >= 0 ? cells[emailIdx] ?? '' : '').trim();
          const phone = telefonoIdx >= 0 ? (cells[telefonoIdx] ?? '').trim() || undefined : undefined;
          if (!email) continue;
          list.push({ fullName, email, phone });
        }
        if (list.length === 0) {
          setParsingError('No se encontraron filas válidas con email.');
        } else {
          setParsedCandidates(list);
          setImportModalOpen(true);
        }
      } catch (err) {
        setParsingError(err instanceof Error ? err.message : 'Error al procesar el CSV');
      } finally {
        setImportingFile(false);
      }
    };
    reader.onerror = () => {
      setParsingError('Error al leer el archivo');
      setImportingFile(false);
    };
    reader.readAsText(file, 'UTF-8');
    e.target.value = '';
  };

  const handleConfirmImport = async () => {
    if (!canWriteCompanyData) return;
    if (!id || !appUser?.companyId || parsedCandidates.length === 0) return;
    if (processStatus === 'closed') {
      window.alert('Este proceso está finalizado. No se pueden importar candidatos.');
      return;
    }
    setImporting(true);
    let successCount = 0;
    const failed: string[] = [];
    try {
      for (const c of parsedCandidates) {
        try {
          await createInviteForCandidate(c.fullName, c.email, c.phone);
          successCount++;
        } catch (err) {
          failed.push(c.fullName || c.email);
        }
      }
      setImportModalOpen(false);
      setParsedCandidates([]);
      setParsingError(null);
      const load = async () => {
        const q = query(collection(db, 'candidates'), where('processId', '==', id));
        const snap = await getDocs(q);
        setCandidates(
          snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            createdAt: d.data().createdAt ?? '',
          })) as Candidate[]
        );
      };
      await load();
      if (failed.length > 0) {
        window.alert(
          `Importados: ${successCount}. Fallaron: ${failed.length} (${failed.slice(0, 3).join(', ')}${failed.length > 3 ? '...' : ''})`
        );
      } else {
        window.alert(`✅ ${successCount} candidato(s) importados correctamente.`);
      }
    } catch (err) {
      console.error('Error al importar candidatos:', err);
      window.alert(err instanceof Error ? err.message : 'Error al importar candidatos');
    } finally {
      setImporting(false);
    }
  };

  const getOrCreateInviteUrl = async (c: Candidate): Promise<string> => {
    if (!canWriteCompanyData) throw new Error('No tienes permisos para generar invitaciones.');
    const companyId = appUser?.companyId ?? c.companyId;
    if (!id || !companyId) throw new Error('Faltan datos');
    const token = generateInviteToken();
    const tokenHash = await hashToken(token);
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + DEFAULT_EXPIRY_DAYS);
    const baseUrl = window.location.origin;

    const invitesSnap = await getDocs(
      query(
        collection(db, 'invites'),
        where('companyId', '==', companyId),
        where('candidateId', '==', c.id)
      )
    );

    if (invitesSnap.empty) {
      const inviteRef = await addDoc(collection(db, 'invites'), {
        companyId,
        processId: id,
        candidateId: c.id,
        tokenHash,
        tokenExpiresAt: expiresAt.toISOString(),
        createdAt: new Date().toISOString(),
      });
      await setDoc(doc(db, 'inviteTokenLookup', tokenHash), {
        inviteId: inviteRef.id,
        candidateId: c.id,
        processId: id,
        companyId,
        tokenExpiresAt: expiresAt.toISOString(),
      });
      return `${baseUrl}/c/${token}`;
    }

    const inviteDoc = invitesSnap.docs[0];
    const oldTokenHash = inviteDoc.data().tokenHash as string;
    await updateDoc(doc(db, 'invites', inviteDoc.id), {
      tokenHash,
      tokenExpiresAt: expiresAt.toISOString(),
    });
    await deleteDoc(doc(db, 'inviteTokenLookup', oldTokenHash));
    await setDoc(doc(db, 'inviteTokenLookup', tokenHash), {
      inviteId: inviteDoc.id,
      candidateId: c.id,
      processId: id,
      companyId,
      tokenExpiresAt: expiresAt.toISOString(),
    });
    return `${baseUrl}/c/${token}`;
  };

  const handleSendEmail = async (c: Candidate) => {
    if (!canWriteCompanyData) return;
    const emailTrim = c.email?.trim();
    if (!emailTrim) {
      window.alert('Este candidato no tiene correo electrónico.');
      return;
    }
    setSendingCandidateId(c.id);
    try {
      const inviteUrl = await getOrCreateInviteUrl(c);
      const sendEmail = httpsCallable(getFunctions(), 'sendAssessmentEmail');
      await sendEmail({
        toEmail: emailTrim,
        employeeName: c.fullName?.trim() || 'Candidato',
        assessmentUrl: inviteUrl,
        testType: 'Evaluación de Selección',
      });
      window.alert('Correo enviado con éxito.');
    } catch (err) {
      console.error('Error al enviar correo:', err);
      window.alert(err instanceof Error ? err.message : 'Error al enviar el correo');
    } finally {
      setSendingCandidateId(null);
    }
  };

  const handleCopyLink = async (c: Candidate) => {
    if (!canWriteCompanyData) return;
    setCopyingLinkId(c.id);
    try {
      const inviteUrl = await getOrCreateInviteUrl(c);
      await navigator.clipboard.writeText(inviteUrl);
      window.alert('Link copiado al portapapeles. Compártelo por WhatsApp, SMS o cualquier medio.');
    } catch (err) {
      console.error('Error al copiar link:', err);
      window.alert(err instanceof Error ? err.message : 'Error al obtener el link');
    } finally {
      setCopyingLinkId(null);
    }
  };

  const handleSendToAll = async () => {
    if (!canWriteCompanyData) return;
    const withEmail = candidates.filter((c) => c.email?.trim());
    if (withEmail.length === 0) {
      window.alert('No hay candidatos con correo electrónico para enviar.');
      return;
    }
    if (
      !window.confirm(
        `Se enviará la invitación por correo a ${withEmail.length} candidato(s). ¿Continuar?`
      )
    )
      return;
    if (!id || !appUser?.companyId) return;
    if (processStatus === 'closed') {
      window.alert('Este proceso está finalizado. No se pueden enviar invitaciones.');
      return;
    }
    setIsSending(true);
    try {
      const sendBatch = httpsCallable(getFunctions(), 'sendCandidateInviteBatch');
      const recipients = withEmail.map((c) => ({
        candidateId: c.id,
        fullName: c.fullName?.trim() || 'Candidato',
        toEmail: c.email!.trim(),
      }));
      const { data } = await sendBatch({
        recipients,
        processId: id,
        companyId: appUser.companyId,
        originUrl: window.location.origin,
      }) as { data: { sentCount: number; failedCount: number; failed: Array<{ candidateName: string; error: string }> } };
      if (data.failedCount > 0) {
        window.alert(
          `Enviados: ${data.sentCount}. Fallaron: ${data.failedCount} (${data.failed.slice(0, 3).map((f) => f.candidateName).join(', ')}${data.failedCount > 3 ? '...' : ''})`
        );
      } else {
        window.alert(`Correos enviados con éxito a ${data.sentCount} candidato(s).`);
      }
    } catch (err) {
      console.error('Error al enviar invitaciones:', err);
      window.alert(err instanceof Error ? err.message : 'Error al enviar el correo');
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    if (!id || !appUser) {
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        const processSnap = await getDoc(doc(db, 'processes', id));
        if (!processSnap.exists() || !canAccessCompany(processSnap.data().companyId)) {
          setAccessDenied(true);
          setLoading(false);
          return;
        }
        setProcessTitle(processSnap.data().title ?? '');
        setProcessStatus(processSnap.data().status ?? null);
        const q = query(
          collection(db, 'candidates'),
          where('processId', '==', id)
        );
        const snap = await getDocs(q);
        setCandidates(
          snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            createdAt: d.data().createdAt ?? '',
          })) as Candidate[]
        );
      } catch (e) {
        if (import.meta.env?.DEV) console.warn('[CandidatesPage] Error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, appUser, canAccessCompany]);

  const statusLabel = (s: Candidate['status']) =>
    s === 'invited' ? 'Invitado' : s === 'in_progress' ? 'En curso' : s === 'completed' ? 'Completado' : 'Expirado';

  if (!id) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/app/processes">← Procesos</Link>
        </Button>
        <p className="text-muted-foreground">Proceso no especificado.</p>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/app/processes">← Procesos</Link>
        </Button>
        <p className="text-muted-foreground">Proceso no encontrado o no tienes permiso para verlo.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to={`/app/processes/${id}`}>← Proceso</Link>
        </Button>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">Candidatos — {processTitle}</h1>
        <div className="flex gap-2">
          {canWriteCompanyData && candidates.some((c) => c.email?.trim()) && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleSendToAll}
              disabled={isSending || processStatus === 'closed'}
            >
              <Mail className="mr-1.5 h-4 w-4" />
              {isSending ? 'Enviando...' : 'Enviar a Todos'}
            </Button>
          )}
          {canWriteCompanyData && processStatus !== 'closed' && (
            <>
              <Button variant="outline" size="sm" onClick={() => setImportModalOpen(true)}>
                <Upload className="mr-1.5 h-4 w-4" />
                Importar CSV
              </Button>
              <Button asChild>
                <Link to={`/app/processes/${id}/candidates/invite`}>Invitar candidato</Link>
              </Button>
            </>
          )}
        </div>
      </div>
      {loading ? (
        <p className="text-muted-foreground">Cargando...</p>
      ) : candidates.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No hay candidatos. Invita a uno para comenzar.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Listado</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-2 font-medium">Nombre</th>
                  <th className="pb-2 font-medium">Email</th>
                  <th className="pb-2 font-medium">Teléfono</th>
                  <th className="pb-2 font-medium">Estado</th>
                  <th className="pb-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((c) => (
                  <tr key={c.id} className="border-b">
                    <td className="py-2">{c.fullName || '—'}</td>
                    <td className="py-2">{c.email || '—'}</td>
                    <td className="py-2">
                      {c.phone ? (
                        <a
                          href={`https://wa.me/${getWhatsAppNumber(c.phone!, companyCountry ?? 'MX')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                        >
                          <MessageCircle className="h-4 w-4" />
                          {(() => {
                            try {
                              return formatPhoneNumberIntl(c.phone!) || c.phone;
                            } catch {
                              return c.phone;
                            }
                          })()}
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-2">{statusLabel(c.status)}</td>
                    <td className="py-2">
                      <span className="flex flex-wrap items-center gap-1">
                        {canWriteCompanyData && (
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(c)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {canWriteCompanyData && processStatus !== 'closed' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopyLink(c)}
                            disabled={copyingLinkId === c.id}
                            title="Copiar link individual (útil si el correo no llega)"
                          >
                            <Link2 className="mr-1 h-3.5 w-3.5" />
                            {copyingLinkId === c.id ? 'Copiando...' : 'Copiar link'}
                          </Button>
                        )}
                        {canWriteCompanyData && c.email?.trim() && processStatus !== 'closed' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSendEmail(c)}
                            disabled={isSending || sendingCandidateId === c.id}
                          >
                            <Mail className="mr-1 h-3.5 w-3.5" />
                            {sendingCandidateId === c.id ? 'Enviando...' : 'Enviar Correo'}
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/app/results?candidate=${c.id}`}>Ver resultados</Link>
                        </Button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={importModalOpen}
        onOpenChange={(open) => {
          setImportModalOpen(open);
          if (!open) {
            setParsedCandidates([]);
            setParsingError(null);
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Importar candidatos desde CSV</DialogTitle>
            <DialogDescription>
              Sube un archivo CSV con columnas: Nombre, Email, Teléfono (sin importar mayúsculas/minúsculas). El email es obligatorio.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {importingFile ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Procesando archivo...</p>
            ) : parsingError ? (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
                <p>{parsingError}</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => {
                    setParsingError(null);
                    setParsedCandidates([]);
                  }}
                >
                  Intentar de nuevo
                </Button>
              </div>
            ) : parsedCandidates.length > 0 ? (
              <>
                <p className="text-sm text-muted-foreground mb-4">
                  Revisa los datos. Si alguna columna no se detectó bien, puedes cancelar, corregir tu archivo y volver a subirlo.
                </p>
                <div className="overflow-x-auto rounded-md border max-h-[400px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-muted/95">
                      <tr className="border-b">
                        <th className="px-4 py-2.5 text-left font-medium">Nombre</th>
                        <th className="px-4 py-2.5 text-left font-medium">Email</th>
                        <th className="px-4 py-2.5 text-left font-medium">Teléfono</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedCandidates.map((c, idx) => (
                        <tr key={idx} className="border-b last:border-0">
                          <td className="px-4 py-2.5">{c.fullName || '—'}</td>
                          <td className="px-4 py-2.5">{c.email}</td>
                          <td className="px-4 py-2.5">{c.phone ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  {parsedCandidates.length} candidato(s) listos para importar.
                </p>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-8">
                <Label htmlFor="csv-file-candidates" className="cursor-pointer">
                  <div className="flex flex-col items-center gap-2 rounded-lg border-2 border-dashed border-muted-foreground/25 px-8 py-12 hover:border-primary/50 hover:bg-muted/50 transition-colors">
                    <Upload className="h-12 w-12 text-muted-foreground" />
                    <span className="text-sm font-medium">Selecciona un archivo CSV</span>
                    <span className="text-xs text-muted-foreground">.csv</span>
                  </div>
                  <input
                    id="csv-file-candidates"
                    type="file"
                    accept=".csv"
                    className="sr-only"
                    onChange={handleFileSelect}
                  />
                </Label>
              </div>
            )}
          </div>
          {parsedCandidates.length > 0 && (
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setImportModalOpen(false);
                  setParsedCandidates([]);
                  setParsingError(null);
                }}
              >
                Cancelar
              </Button>
              <Button onClick={handleConfirmImport} disabled={importing}>
                {importing ? 'Importando...' : 'Confirmar y Guardar'}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={editModalOpen}
        onOpenChange={(open) => {
          setEditModalOpen(open);
          if (!open) {
            setEditingId(null);
            setFormFullName('');
            setFormEmail('');
            setFormPhone('');
          }
        }}
      >
        <DialogContent>
          <form onSubmit={handleUpdateCandidate}>
            <DialogHeader>
              <DialogTitle>Editar candidato</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-fullName">Nombre</Label>
                <Input
                  id="edit-fullName"
                  value={formFullName}
                  onChange={(e) => setFormFullName(e.target.value)}
                  placeholder="Nombre del candidato"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                  placeholder="candidato@ejemplo.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Teléfono</Label>
                <div
                  className="[&_.PhoneInput]:flex [&_.PhoneInput]:gap-2 [&_.PhoneInputInput]:flex [&_.PhoneInputInput]:h-10 [&_.PhoneInputInput]:flex-1 [&_.PhoneInputInput]:rounded-md [&_.PhoneInputInput]:border [&_.PhoneInputInput]:border-input [&_.PhoneInputInput]:bg-background [&_.PhoneInputInput]:px-3 [&_.PhoneInputInput]:py-2 [&_.PhoneInputInput]:text-sm [&_.PhoneInputCountryIcon]:!h-5 [&_.PhoneInputCountryIcon]:!w-[1.875rem]"
                >
                  <PhoneInput
                    defaultCountry={(companyCountry === 'OTHER' ? 'US' : companyCountry) || 'MX'}
                    value={formPhone}
                    onChange={(val) => setFormPhone(val ?? '')}
                    placeholder="+52 55 1234 5678"
                  />
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditModalOpen(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isUpdating}>
                {isUpdating ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
